import React from 'react';

import { useAppSelector } from '@fd/customHooks/useAppSelector';
import { getActiveLanguage } from 'react-localize-redux';
import { useParams } from 'react-router';

import MicroFrontend from '@fd/ui/MicroFrontend/MicroFrontend';

import { getApiEndPoint } from '../../helpers/apibase';
import { isProductionPortalHost } from '../../helpers/portalHost';

export const FLIPDISH_AGENT_CONTAINER_ID = 'flipdish-agent-root';
export const FLIPDISH_AGENT_WINDOW_NAME = 'flipdish-agent';

/**
 * Hosts the flipdish-agent chat MFE as a chrome-free widget surface (`data-mfe-route=widget`).
 * Uses a dedicated container id so it can sit beside a page-level MFE.
 *
 * Hosted Portal points the widget at the cookie-auth governance route
 * (`api.portal.flipdish.com/flipdish-agent/`), same pattern as clients-onboarding.
 * Local Vite uses the MFE default `/portal-agent` (Portal proxy).
 */
export const FlipdishAgentMFE = () => {
  const languageCode = useAppSelector((state) => getActiveLanguage(state.locale));
  const { appId: routeAppId } = useParams<{ appId?: string }>();
  const currentApp = useAppSelector((state) => state.currentApp);
  const accountId = useAppSelector((state) => state.account?.AccountId);

  const appId = currentApp?.AppId ?? routeAppId ?? '';
  const userId = accountId != null ? String(accountId) : '';

  const isLocalDev =
    !isProductionPortalHost() &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  const streamUrl = isLocalDev
    ? undefined
    : `${getApiEndPoint().replace(/\/$/, '')}/flipdish-agent/`;

  const partialIndexUrl = isProductionPortalHost()
    ? 'https://cdn.flipdish.com/flipdish-agent'
    : 'https://clients-integration.portal-mfs.flipdishdev.com/flipdish-agent/prod-staging';

  return (
    <MicroFrontend
      {...(isLocalDev
        ? {
            // Match Portal's hostname (127.0.0.1 vs localhost) — mixed hosts break Vite module loads.
            indexUrl: `http://${window.location.hostname}:5173/src/main.tsx`,
            enableManifestFile: false,
          }
        : {
            partialIndexUrl,
            enableManifestFile: true,
          })}
      microFrontendWindowName={FLIPDISH_AGENT_WINDOW_NAME}
      containerId={FLIPDISH_AGENT_CONTAINER_ID}
      language={languageCode}
      portalBaseUrl={`/${appId}`}
      className="h-full min-h-0"
      otherProps={{
        'data-fd': 'flipdish-agent-micro-frontend',
        'data-mfe-route': 'widget',
        ...(streamUrl ? { 'data-stream-url': streamUrl } : {}),
        ...(userId ? { 'data-user-id': userId } : {}),
      }}
    />
  );
};

export default FlipdishAgentMFE;
